import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { nrPageAction, nrNoticeError } from '../util-helpers/newrelic';
import { getAccessToken, getApiBase, getOrgId, getUserId, generateHeaders } from '../fvid-auth/userSession';
import { isNullOrUndefined, safeRedirect } from '../util-helpers/common';
import { AddInStorage } from '../storage/AddInStorage';

const PLEASE_AUTHENTICATE_ERROR_MESSAGE = 'please authenticate';

/**
 * Utility function to clear authentication state (similar to contextSignout)
 * This clears storage and redirects to origin, mimicking the auth context signout
 */
const clearAuthAndRedirect = () => {
  AddInStorage.clear();
  safeRedirect(window.location.origin);
};

/**
 * Creates an HTTP client with authentication, error handling, and logging
 * This is the main HTTP client for making API requests in the application
 */
export const createHttpClient = (clientIpAddress: string = '127.0.0.1'): AxiosInstance => {
  const client = axios.create({
    baseURL: getApiBase(),
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'x-fv-application': 'outlook-addin',
    },
  });

  // Request interceptor for authentication
  client.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      try {
        if (isNullOrUndefined(getOrgId()) || isNullOrUndefined(getUserId())) {
          console.warn('OrgId or userId is not set. Retrieving new orgId and userId');
          await generateHeaders(true);
        }

        const accessToken = await getAccessToken();
        return {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
            'x-fv-clientIp': clientIpAddress,
            'x-fv-orgId': getOrgId() || '',
            'x-fv-userId': getUserId() || '',
          },
        };
      } catch (error) {
        const axiosError = error as any;
        const status = axiosError?.response?.status;
        const hasRequest = !!axiosError?.request;
        const hasResponse = !!axiosError?.response;
        const isNetworkError = hasRequest && !hasResponse;

        // For status 0 (network/CORS), 503/504 or network errors, reject the request entirely
        if (status === 0 || status === 503 || status === 504 || isNetworkError) {
          console.error('Service unavailable when fetching user org data:', status || 'network error');
          return Promise.reject(error);
        }

        console.warn('Auth setup not available, proceeding without auth headers:', error instanceof Error ? error.message : error);
        // Continue without auth headers - will trigger 401 which is handled by response interceptor
        // x-fv-application header is already included from default headers
      }

      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      // Check for HTML responses being returned as JSON
      if (response.data && typeof response.data === 'string') {
        if (response.data.includes('<!DOCTYPE html>')
            || response.data.includes('<html>')
            || response.data.startsWith('<')) {

          const error = new Error(response.data.substring(0, 100) + '...');
          nrNoticeError(error, {
            type: 'HTMLInJSONResponse',
            url: window.location.href,
            endpoint: response.config.url || 'unknown'
          });

          throw error;
        }
      }

      return response;
    },
    (error: AxiosError) => {
      const pageUrl = window.location.href;
      const endpoint = error.config?.url || 'unknown';

      if (error.response) {
        const status = error.response.status;

        switch (status) {
          case 401:
            nrPageAction('httpClient', {
              message: `Unauthorized request to ${endpoint}: ${error.message}`,
              url: pageUrl,
              status
            });
            break;

          case 403:
            nrPageAction('httpClient', {
              message: `Forbidden request to ${endpoint}: ${error.message}`,
              url: pageUrl,
              status
            });
            break;

          case 404:
            nrPageAction('httpClient', {
              message: `Not found: ${endpoint}`,
              url: pageUrl,
              status
            });
            break;

          case 500:
          case 502:
          case 503:
          case 504:
            nrPageAction('httpClient', {
              message: `Server error ${status} for ${endpoint}: ${error.message}`,
              url: pageUrl,
              status
            });
            break;

          default:
            nrPageAction('httpClient', {
              message: `HTTP ${status} error for ${endpoint}: ${error.message}`,
              url: pageUrl,
              status
            });
        }
      } else if (error.request) {
        nrPageAction('authenticatedHttpClient', {
          message: `Network error for ${endpoint}: ${error.message}`,
          url: pageUrl,
          type: 'NetworkError'
        });
      } else {
        nrPageAction('authenticatedHttpClient', {
          message: `Request setup error for ${endpoint}: ${error.message}`,
          url: pageUrl,
          type: 'RequestError'
        });
      }

      // Check for authentication error messages
      if (error.message?.toLowerCase().includes(PLEASE_AUTHENTICATE_ERROR_MESSAGE)) {
        nrPageAction('authenticatedHttpClient', {
          message: `Authentication required for ${endpoint}`,
          url: pageUrl
        });
        clearAuthAndRedirect();
      }

      if (error.message?.toLowerCase().includes('accessnotallowedexception')) {
        nrPageAction('authenticatedHttpClient', {
          message: `Access not allowed for ${endpoint}`,
          url: pageUrl
        });
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Export types for use in other files
export type { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';
